import { BrowserModule, Title } from '@angular/platform-browser';
import { Routes, RouterModule } from '@angular/router';
import { NgModule, NO_ERRORS_SCHEMA } from '@angular/core';
import { MatSliderModule } from '@angular/material/slider';
import { MatTabsModule } from '@angular/material/tabs';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { HighchartsChartModule } from 'highcharts-angular';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { NotifierModule, NotifierOptions } from 'angular-notifier';

import { MDBBootstrapModule } from 'angular-bootstrap-md';

import { AppComponent } from './app.component';
import { HeaderComponent } from './header/header.component';
import { FooterComponent } from './footer/footer.component';
import { HomeComponent } from './home/home.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NotFoundComponent } from './not-found/not-found.component';
import { ToolsComponent } from './tools/tools.component';
import { ResourcesComponent } from './resources/resources.component';
import { SupportComponent } from './support/support.component';
import { DatabaseComponent } from './database/database.component';
import { MyPortfolioComponent } from './tools/my-portfolio/my-portfolio.component';
import { MyPortfolioCurrentComponent } from './tools/my-portfolio/my-portfolio-current/my-portfolio-current.component';
import { MyPortfolioCreateComponent } from './tools/my-portfolio/my-portfolio-create/my-portfolio-create.component';
import { MyPortfolioUpdateComponent } from './tools/my-portfolio/my-portfolio-update/my-portfolio-update.component';
import { AnalyticsComponent } from './tools/analytics/analytics.component';
import { InvestableSolutionComponent } from './tools/investable-solution/investable-solution.component';
import { RiskManagementComponent } from './tools/risk-management/risk-management.component';
import { PositionKeepingComponent } from './tools/position-keeping/position-keeping.component';
import { SignInComponent } from './auth/sign-in/sign-in.component';
import { RegisterComponent } from './auth/register/register.component';
import { HttpClientModule } from '@angular/common/http';
import { AuthService } from './services/auth.service';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AlertMessageComponent } from './blocks/alert-message/alert-message.component';
import { LineButtonComponent } from './blocks/line-button/line-button.component';
import { TitleComponent } from './blocks/title/title.component';
import { PeersWidgetComponent } from './widgets/peers-widget/peers-widget.component';
import { PeersWidgetChartComponent } from './widgets/peers-widget/peers-widget-chart/peers-widget-chart.component';
import { PeersWidgetSummaryComponent } from './widgets/peers-widget/peers-widget-summary/peers-widget-summary.component';
import { RatingPipe } from './pipes/rating.pipe';
import { AnalyticsPerformanceComponent } from './tools/analytics/analytics-performance/analytics-performance.component';
import { BasicLineChartComponent } from './charts/basic-line-chart/basic-line-chart.component';
import { BasicBarChartComponent } from './charts/basic-bar-chart/basic-bar-chart.component';
import { AnalyticsFactorAnalysisComponent } from './tools/analytics/analytics-factor-analysis/analytics-factor-analysis.component';
import { AnalyticsRobustnessComponent } from './tools/analytics/analytics-robustness/analytics-robustness.component';
import { LttrStrategyService } from './services/lttr-strategy.service';
import { PortfolioModelsPerformanceComponent } from './resources/portfolio-models/portfolio-models-performance/portfolio-models-performance.component';
import { PortfolioModelsFactorAnalysisComponent } from './resources/portfolio-models/portfolio-models-factor-analysis/portfolio-models-factor-analysis.component';
import { PortfolioModelsRobustnessComponent } from './resources/portfolio-models/portfolio-models-robustness/portfolio-models-robustness.component';
import { ClustersService } from './services/clusters.service';
import { MatCardModule } from "@angular/material/card";
import { RiskManagementMetricsComponent } from './tools/risk-management/risk-management-metrics/risk-management-metrics.component';
import { RiskManagementInsuranceComponent } from './tools/risk-management/risk-management-insurance/risk-management-insurance.component';
import { RiskManagementSimulationComponent } from './tools/risk-management/risk-management-simulation/risk-management-simulation.component';
import { StrategyCustomizeComponent } from './tools/strategy-builder/strategy-customize/strategy-customize.component';
import { NotificationsService } from './services/notifications.service';

import { MatNativeDateModule } from '@angular/material/core';
import { MatStepperModule } from "@angular/material/stepper";
import { MatIconModule } from "@angular/material/icon";
import { MatListModule } from "@angular/material/list";
import { MatExpansionModule } from "@angular/material/expansion";
import { FrequencyBadgeComponent } from './blocks/frequency-badge/frequency-badge.component';
import { SectionComponent } from './blocks/section/section.component';
import { SynthesisComponent } from './blocks/synthesis/synthesis.component';
import { ShadowDivComponent } from './blocks/shadow-div/shadow-div.component';
import { ButtonComponent } from './blocks/button/button.component';
import { ApiRequestsService } from './services/api-requests.service';
import { MatSidenavModule } from "@angular/material/sidenav";
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatPaginatorModule } from "@angular/material/paginator";
import { MatTableModule } from "@angular/material/table";
import { MatSortModule } from "@angular/material/sort";
import { ListsService } from './services/lists.service';
import { IframeComponent } from './blocks/iframe/iframe.component';
import { SafePipe } from './pipes/safe.pipe';
import { NewsFeedComponent } from './blocks/news-feed/news-feed.component';
import { TitleJumboComponent } from "./blocks/title-jumbo/title-jumbo.component";
import { MatButtonModule } from "@angular/material/button";
import { RatingSpanComponent } from './blocks/rating-span/rating-span.component';
import { PortfolioModelsComponent } from './resources/portfolio-models/portfolio-models.component';
import { GlossaryComponent } from './resources/glossary/glossary.component';
import { PapersComponent } from './resources/papers/papers.component';
import { WebinarsComponent } from './resources/webinars/webinars.component';
import { EventsComponent } from './resources/events/events.component';
import { DashboardComponent } from './tools/dashboard/dashboard.component';
import { SidebarComponent } from './tools/sidebar/sidebar.component';
import { MatDividerModule } from "@angular/material/divider";
import { FlexLayoutModule } from "@angular/flex-layout";
import { BasicAreaChartComponent } from './charts/basic-area-chart/basic-area-chart.component';
import { ChartsCardComponent } from './charts/charts-card/charts-card.component';
import { BasicDonutChartComponent } from './charts/basic-donut-chart/basic-donut-chart.component';
import { DashboardService } from "./services/dashboard.service";
import { SidebarResourcesComponent } from './resources/sidebar-resources/sidebar-resources.component';
import { CardAumComponent } from './tools/dashboard/cards/card-aum/card-aum.component';
import { CardChartIntensityComponent } from './tools/dashboard/cards/card-chart-intensity/card-chart-intensity.component';
import { CardChartRatingsComponent } from './tools/dashboard/cards/card-chart-ratings/card-chart-ratings.component';
import { StrategyUniverseComponent } from './tools/strategy-builder/strategy-universe/strategy-universe.component';
import { StrategiesService } from './services/strategies.service';
import { StrategyBuilderComponent } from './tools/strategy-builder/strategy-builder.component';
import { SpinnerComponent } from './blocks/spinner/spinner.component';
import { StyleBoxComponent } from './charts/style-box/style-box.component';
import { MathjaxComponent } from './blocks/mathjax/mathjax.component';
import { ConfigService } from './services/config.service';
import { PositionKeepingPerformanceComponent } from './tools/position-keeping/position-keeping-performance/position-keeping-performance.component';
import { PositionKeepingProtectionComponent } from './tools/position-keeping/position-keeping-protection/position-keeping-protection.component';
import { PositionKeepingRebalancingComponent } from './tools/position-keeping/position-keeping-rebalancing/position-keeping-rebalancing.component';
import { NotificationsComponent } from './blocks/notifications/notifications.component';
import { NavigationItemComponent } from './blocks/navigation-item/navigation-item.component';
import { MapChartComponent } from './charts/map-chart/map-chart.component';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { StrategyImplementComponent } from './tools/strategy-builder/strategy-implement/strategy-implement.component';
import { NotifierComponent } from './shared/notifier/notifier.component';
import { UserService } from './services/user.service';
import { UserPictureComponent } from './shared/user-picture/user-picture.component';
import { InvestableConstraintsComponent } from './tools/investable-solution/investable-constraints/investable-constraints.component';
import { InvestableOptimizationComponent } from './tools/investable-solution/investable-optimization/investable-optimization.component';
import { InvestableImplementComponent } from './tools/investable-solution/investable-implement/investable-implement.component';
import { HistoricalBreakdownChartComponent } from './charts/historical-breakdown-chart/historical-breakdown-chart.component';
import { DatePickerComponent } from './blocks/date-picker/date-picker.component';
import { MatDatepickerModule } from "@angular/material/datepicker";

const appRoutes: Routes = [
  { path: 'home', component: HomeComponent },
  { path: 'tools/:tab/:step', canActivate: [AuthService], component: ToolsComponent },
  { path: 'tools/:tab', canActivate: [AuthService], component: ToolsComponent },
  { path: 'tools', canActivate: [AuthService], component: ToolsComponent },
  { path: 'resources/:tab', canActivate: [AuthService], component: ResourcesComponent },
  { path: 'resources', canActivate: [AuthService], component: ResourcesComponent },
  { path: 'support', component: SupportComponent },
  { path: 'database', canActivate: [AuthService], component: DatabaseComponent },
  { path: 'sign-in', component: SignInComponent },
  { path: 'register', component: RegisterComponent },
  { path: '', redirectTo: 'home', pathMatch: 'full' },
  { path: 'not-found', component: NotFoundComponent },
  { path: '**', redirectTo: '/not-found' }
];

/**
 * Custom angular notifier options
 */
const customNotifierOptions: NotifierOptions = {
  position: {
    horizontal: {
      position: 'right',
      distance: 12,
    },
    vertical: {
      position: 'top',
      distance: 76,
      gap: 10,
    },
  },
  theme: 'material',
  behaviour: {
    autoHide: 5000,
    onClick: 'hide',
    onMouseover: 'pauseAutoHide',
    showDismissButton: true,
    stacking: 10,
  },
  animations: {
    enabled: true,
    show: {
      preset: 'slide',
      speed: 300,
      easing: 'ease',
    },
    hide: {
      preset: 'fade',
      speed: 300,
      easing: 'ease',
      offset: 50,
    },
    shift: {
      speed: 300,
      easing: 'ease',
    },
    overlap: 150,
  },
};

const material = [
  MatSidenavModule
];

@NgModule({
    declarations: [
        AppComponent,
        HeaderComponent,
        FooterComponent,
        HomeComponent,
        NotFoundComponent,
        ToolsComponent,
        ResourcesComponent,
        SupportComponent,
        DatabaseComponent,
        MyPortfolioComponent,
        MyPortfolioCurrentComponent,
        MyPortfolioCreateComponent,
        MyPortfolioUpdateComponent,
        AnalyticsComponent,
        InvestableSolutionComponent,
        RiskManagementComponent,
        PositionKeepingComponent,
        SignInComponent,
        RegisterComponent,
        AlertMessageComponent,
        LineButtonComponent,
        TitleComponent,
        PeersWidgetComponent,
        PeersWidgetChartComponent,
        PeersWidgetSummaryComponent,
        RatingPipe,
        AnalyticsPerformanceComponent,
        BasicLineChartComponent,
        BasicBarChartComponent,
        AnalyticsFactorAnalysisComponent,
        AnalyticsRobustnessComponent,
        PortfolioModelsPerformanceComponent,
        PortfolioModelsFactorAnalysisComponent,
        PortfolioModelsRobustnessComponent,
        RiskManagementMetricsComponent,
        RiskManagementInsuranceComponent,
        RiskManagementSimulationComponent,
        StrategyCustomizeComponent,
        FrequencyBadgeComponent,
        SectionComponent,
        SynthesisComponent,
        ShadowDivComponent,
        ButtonComponent,
        RegisterComponent,
        IframeComponent,
        SafePipe,
        NewsFeedComponent,
        TitleJumboComponent,
        RatingSpanComponent,
        PortfolioModelsComponent,
        GlossaryComponent,
        PapersComponent,
        WebinarsComponent,
        EventsComponent,
        DashboardComponent,
        SidebarComponent,
        BasicAreaChartComponent,
        ChartsCardComponent,
        BasicDonutChartComponent,
        SidebarResourcesComponent,
        CardAumComponent,
        CardChartIntensityComponent,
        CardChartRatingsComponent,
        StrategyBuilderComponent,
        StrategyUniverseComponent,
        SpinnerComponent,
        StyleBoxComponent,
        MathjaxComponent,
        PositionKeepingPerformanceComponent,
        PositionKeepingProtectionComponent,
        PositionKeepingRebalancingComponent,
        NotificationsComponent,
        NavigationItemComponent,
        MapChartComponent,
        StrategyImplementComponent,
        NotifierComponent,
        UserPictureComponent,
        InvestableConstraintsComponent,
        InvestableOptimizationComponent,
        InvestableImplementComponent,
        HistoricalBreakdownChartComponent,
        DatePickerComponent,
    ],
    imports: [
      BrowserModule,
      FormsModule,
      ReactiveFormsModule,
      HttpClientModule,
      RouterModule.forRoot(appRoutes),
      MDBBootstrapModule.forRoot(),
      MatSliderModule,
      MatTabsModule,
      MatFormFieldModule,
      MatSelectModule,
      MatCheckboxModule,
      MatRadioModule,
      MatInputModule,
      BrowserAnimationsModule,
      HighchartsChartModule,
      MatCardModule,
      MatStepperModule,
      MatIconModule,
      MatListModule,
      MatExpansionModule,
      MatSidenavModule,
      MatToolbarModule,
      MatPaginatorModule,
      MatTableModule,
      MatSortModule,
      MatButtonModule,
      FlexLayoutModule,
      MatDividerModule,
      MatProgressSpinnerModule,
      MatSlideToggleModule,
      DragDropModule,
      MatDatepickerModule,
      MatNativeDateModule,
      NotifierModule.withConfig(customNotifierOptions),
    ],
  schemas: [ NO_ERRORS_SCHEMA ],
  providers: [
    Title,
    AuthService,
    LttrStrategyService,
    ClustersService,
    ApiRequestsService,
    ListsService,
    DashboardService,
    StrategiesService,
    ConfigService,
    NotificationsService,
    UserService,
  ],
  bootstrap: [
    AppComponent
  ]
})
export class AppModule { }
